import { useEffect, useMemo } from "react";
import { Flex, Text } from "@radix-ui/themes";
import { parseISO } from "date-fns";

import { useCountdown } from "@/hooks/useCountdown";
import PreReleaseNotifyForm from "../PreReleaseNotifyForm";
import CountdownUnit from "./CountdownUnit";

type PreReleaseBlockProps = {
  releaseDate: string;
  albumId: string;
  onExpired?: () => void;
};

export default function PreReleaseBlock({
  releaseDate,
  albumId,
  onExpired,
}: PreReleaseBlockProps) {
  const targetDate = useMemo(() => parseISO(releaseDate), [releaseDate]);
  const { days, hours, minutes, seconds, isExpired } = useCountdown(targetDate);

  useEffect(() => {
    if (isExpired) onExpired?.();
  }, [isExpired, onExpired]);

  if (isExpired) return null;

  const now = new Date();
  const isSameDay =
    targetDate.getFullYear() === now.getFullYear() &&
    targetDate.getMonth() === now.getMonth() &&
    targetDate.getDate() === now.getDate();

  const formattedDate = isSameDay
    ? targetDate.toLocaleString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : targetDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });

  return (
    <Flex
      direction="column"
      align="center"
      className="gap-5 py-5 px-5 rounded-xl border border-white/10 bg-white/5 backdrop-blur-md"
    >
      {/* Label + Date */}
      <Flex direction="column" align="center" className="gap-1">
        <Text className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/50">
          Dropping
        </Text>
        <Text className="text-[20px] lg:text-[22px] font-bold text-white font-figtree">
          <time dateTime={releaseDate}>{formattedDate}</time>
        </Text>
      </Flex>

      {/* Countdown */}
      <Flex className="gap-2 lg:gap-3">
        <CountdownUnit value={days} label="Days" />
        <CountdownUnit value={hours} label="Hrs" />
        <CountdownUnit value={minutes} label="Min" />
        <CountdownUnit value={seconds} label="Sec" />
      </Flex>

      {/* Divider */}
      <div className="w-full h-px bg-white/10" />

      {/* Notify CTA */}
      <PreReleaseNotifyForm albumId={albumId} />
    </Flex>
  );
}
